/**
 * UserMultiSelect - Seletor de usuários (único ou múltiplo) do designer de Workflow
 *
 * Reaproveita o mesmo padrão visual dos filtros do CRM/Kanban (Popover + Command
 * + SmartAvatar) e a MESMA fonte de dados (`useTenantMembers`, RPC server-side),
 * portanto não cria consultas novas nem duplica cache.
 *
 * Substitui os antigos campos de texto que exigiam colar o UUID do usuário.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial (multi-seleção de destinatários)
 */

import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, UserCheck, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { useTenantMembers } from '@/hooks/useTenantMembers';

interface UserMultiSelectProps {
  /** Lista de user_ids selecionados (em modo único, use no máximo 1 item) */
  value: string[];
  onChange: (ids: string[]) => void;
  /** Quando falso, comporta-se como seleção única */
  multiple?: boolean;
  placeholder?: string;
  className?: string;
}

/** Seletor de usuários do tenant com busca, avatar e chips de seleção */
export function UserMultiSelect({
  value,
  onChange,
  multiple = true,
  placeholder = 'Selecione o usuário',
  className,
}: UserMultiSelectProps) {
  const [aberto, setAberto] = useState(false);
  const { data: membros = [], isLoading } = useTenantMembers({ onlyActive: true });

  // Índice auxiliar para renderizar os chips sem varrer a lista a cada item
  const porId = useMemo(() => {
    const mapa = new Map<string, (typeof membros)[number]>();
    membros.forEach((m) => mapa.set(m.user_id, m));
    return mapa;
  }, [membros]);

  const alternar = (userId: string) => {
    if (!multiple) {
      onChange(value.includes(userId) ? [] : [userId]);
      setAberto(false);
      return;
    }
    onChange(value.includes(userId) ? value.filter((id) => id !== userId) : [...value, userId]);
  };

  const rotuloResumo = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const m = porId.get(value[0]);
      return m?.full_name || m?.email || 'Usuário';
    }
    return `${value.length} usuários selecionados`;
  };

  return (
    <div className={cn('space-y-1.5', className)}>
      <Popover open={aberto} onOpenChange={setAberto}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 w-full justify-between font-normal">
            <span className="flex min-w-0 items-center gap-2">
              <UserCheck className="h-4 w-4 shrink-0 opacity-70" />
              <span className="truncate">{isLoading ? 'Carregando...' : rotuloResumo()}</span>
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Buscar usuário..." />
            <CommandList className="max-h-64">
              <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>
              <CommandGroup>
                {membros.map((m) => {
                  const nome = m.full_name || m.email || 'Usuário';
                  const marcado = value.includes(m.user_id);
                  return (
                    <CommandItem
                      key={m.user_id}
                      value={`${nome} ${m.email ?? ''}`}
                      onSelect={() => alternar(m.user_id)}
                      className="gap-2"
                    >
                      <SmartAvatar src={m.avatar_url} name={nome} size="xs" use3DFallback />
                      <span className="min-w-0 flex-1 truncate">{nome}</span>
                      {marcado && <Check className="h-4 w-4 text-primary" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Chips das seleções (apenas no modo múltiplo, para leitura rápida) */}
      {multiple && value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((id) => {
            const m = porId.get(id);
            return (
              <Badge key={id} variant="secondary" className="gap-1 pr-1 text-[11px]">
                {m?.full_name || m?.email || id.slice(0, 8).toUpperCase()}
                <button
                  type="button"
                  className="rounded-sm hover:bg-muted"
                  onClick={() => onChange(value.filter((v) => v !== id))}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}
